import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Redirect } from 'react-router';
import { isEmpty } from 'lodash';
import styled from '@emotion/styled';

import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    HeadingText50 as StepTitle,
    RadioGroup,
    pxToRem,
} from '@modernhealth/vital-components';

import { mediaQueries } from 'src/shared/styles/variables';
import { navigationService } from 'src/config/rootService';
import { CareNavigationRequestContactPreference } from 'src/generated/webapp_gql';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { CareNavigationRequest } from '../models/careNavigation';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { ButtonGroupTestID, CareNavigationContactMethodStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    minWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
});

const FormContainer = styled.div({
    textAlign: 'left',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

export const CareNavigationContactMethodStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest | undefined>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();
    const { t } = useTranslation();
    const [requestState, setRequestState] = useState<CareNavigationRequest | undefined>(
        location.state,
    );

    const options = [
        {
            label: t('careNavigation:care_navigation_contact_method_step.answer_one_title'),
            value: CareNavigationRequestContactPreference.Email,
        },
        {
            label: t('careNavigation:care_navigation_contact_method_step.answer_two_title'),
            value: CareNavigationRequestContactPreference.Phone,
        },
    ];

    const isContinueButtonDisabled = () => {
        return requestState?.contactPreference === '';
    };

    const handleContinue = () => {
        navigationService.navigate('/care-navigation-contact-information', requestState);
    };

    const handleBack = () => {
        navigationService.navigate('/care-navigation-goals', requestState);
    };

    if (!isUserEnabledFlow) {
        return <Redirect to="/dashboard" />;
    }

    if (!requestState || !location.state || isEmpty(location.state)) {
        return <Redirect to="/care-navigation-request" />;
    }

    return (
        <Wrapper>
            <CareNavigationSteps step={3} flow={requestState.flow} />

            <TitleContainer>
                <StepTitle>
                    {t('careNavigation:care_navigation_contact_method_step.question')}
                </StepTitle>
                <BodyTextMedium>
                    {t('careNavigation:care_navigation_contact_method_step.sub_title')}
                </BodyTextMedium>
            </TitleContainer>

            <FormContainer>
                <RadioGroup
                    options={options}
                    selected={requestState.contactPreference as string}
                    onChange={value =>
                        setRequestState({
                            ...requestState,
                            contactPreference: value as CareNavigationRequestContactPreference,
                        })
                    }
                    testID={CareNavigationContactMethodStepTestId.RadioGroup}
                />

                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={isContinueButtonDisabled()}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
